import { useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { BRAND, NAV_LINKS } from "../../data/content";
import { scrollToHash } from "@/lib/scrollToHash";
import lionHeadLogo from "../../assets/lion-head-logo.png";

const NavLink = ({ href, label, testId }) => (
  <a
    href={href}
    onClick={(e) => scrollToHash(e, href)}
    data-testid={testId}
    className="group relative shrink-0 whitespace-nowrap py-2 text-[11.5px] font-medium uppercase tracking-[0.8px] text-[rgba(243,239,231,.72)] transition-colors hover:text-[#f3efe7]"
  >
    {label}
    <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-[#c9a24d] transition-all duration-300 ease-out group-hover:w-full" />
  </a>
);

const Logo = ({ testId }) => (
  <a
    href="#home"
    onClick={(e) => scrollToHash(e, "#home")}
    data-testid={testId}
    className="flex shrink-0 items-center gap-2.5 whitespace-nowrap"
  >
    <img
      src={lionHeadLogo}
      alt="Blacklion lion emblem"
      className="h-7 w-7 shrink-0 object-contain"
    />
    <div className="flex flex-col leading-[1.05]">
      <b className="bg-[linear-gradient(180deg,#f1dfa8,#b98e3e)] bg-clip-text font-hero text-[16px] font-bold tracking-[0.5px] text-transparent">
        BLACKLION
      </b>
      <span className="mt-0.5 text-[8px] tracking-[1.5px] text-[rgba(233,217,171,.55)]">
        CONSTRUCTION GROUP
      </span>
    </div>
  </a>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const handleMobileNav = (e, href) => {
    scrollToHash(e, href);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 border-b border-[#c9a24d]/25 bg-[#0a0a0a]"
    >
      <nav className="flex h-[76px] items-center justify-between gap-4 px-5 sm:px-[28px]">
        <Logo testId="nav-logo" />

        <div className="hidden shrink-0 items-center gap-[18px] lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.href}
              href={l.href}
              label={l.label}
              testId={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
            />
          ))}
        </div>

        <Button
          asChild
          data-testid="nav-cta-quote"
          className="hidden h-auto shrink-0 rounded-[2px] bg-[#c9a24d] px-[18px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#151310] shadow-none hover:bg-[#c9a24d]/90 lg:inline-flex"
        >
          <a href="#contact" onClick={(e) => scrollToHash(e, "#contact")}>Get a Free Quote</a>
        </Button>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open menu"
              data-testid="nav-mobile-menu-trigger"
              className="flex h-10 w-10 shrink-0 items-center justify-center text-[#f3efe7] lg:hidden"
            >
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            data-testid="nav-mobile-menu"
            className="w-[82vw] max-w-sm border-l border-[#c9a24d]/25 bg-[#0a0a0a] p-0 sm:max-w-sm"
          >
            <SheetTitle className="sr-only">Site navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Links to jump to sections of the Blacklion Construction Group site.
            </SheetDescription>

            <div className="flex h-full flex-col px-6 pb-8 pt-16">
              <div className="mb-2">
                <Logo testId="nav-mobile-logo" />
              </div>

              <div className="mt-6 flex flex-1 flex-col gap-1 border-t border-[#c9a24d]/15 pt-6">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={(e) => handleMobileNav(e, l.href)}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                    className="border-b border-white/10 py-4 text-sm font-medium uppercase tracking-[0.8px] text-[rgba(243,239,231,.85)] transition-colors hover:text-[#c9a24d]"
                  >
                    {l.label}
                  </a>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={BRAND.phoneHref}
                  data-testid="nav-mobile-call"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white"
                >
                  <Phone size={16} />
                  {BRAND.phone}
                </a>
                <a
                  href="#contact"
                  onClick={(e) => handleMobileNav(e, "#contact")}
                  data-testid="nav-mobile-cta-quote"
                  className="inline-flex items-center justify-center rounded-[2px] bg-[#c9a24d] px-6 py-3 text-xs font-bold uppercase tracking-[1px] text-[#151310]"
                >
                  Get a Free Quote
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};
